import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQuery, useMutation } from '@tanstack/react-query'
import { leadsApi, type Lead } from '../api/leads'
import { customStatusesApi } from '../api/settings'
import { type Organization } from '../api/contacts'
import { Modal } from './ui/Modal'
import { DropdownSelect, type DropdownOption } from './ui/DropdownSelect'
import { LEAD_SOURCE_OPTIONS } from '../lib/leadSource'
import toast from 'react-hot-toast'

export interface CreateLeadFormData {
  firstName: string
  lastName: string
  email?: string
  phone?: string
  organizationId: string
  status?: string
  source?: string
}

/**
 * Shared "New Lead" modal. Used by the Leads page and the WhatsApp new-chat flow.
 * - `onSuccess` keeps the original Leads-page contract (no args).
 * - `onCreated` additionally hands back the created lead (used to link a WhatsApp chat).
 * - `defaultPhone` pre-fills the phone field when opening (e.g. a typed WhatsApp number).
 */
export function CreateLeadModal({ open, onClose, companyId, onSuccess, onCreated, orgs, defaultPhone, defaultFirstName }: {
  open: boolean
  onClose: () => void
  companyId: string
  onSuccess?: () => void
  onCreated?: (lead: Lead) => void
  orgs: Organization[]
  defaultPhone?: string
  defaultFirstName?: string
}) {
  const { register, handleSubmit, reset, control, watch, formState: { errors } } = useForm<CreateLeadFormData>({
    defaultValues: { status: '', source: '' },
  })

  // A lead is reachable by email or by phone — exactly one of the two is enough.
  // Both fields validate against the same condition so filling either one clears
  // the error on both.
  const email = watch('email')
  const phone = watch('phone')
  const hasContact = !!(email?.trim() || phone?.trim())
  const showContactError = !hasContact && !!(errors.email || errors.phone)

  // When opened with pre-filled values (WhatsApp flow), seed the form cleanly.
  useEffect(() => {
    if (open && (defaultPhone !== undefined || defaultFirstName !== undefined)) {
      reset({ status: '', phone: defaultPhone, firstName: defaultFirstName })
    }
  }, [open, defaultPhone, defaultFirstName, reset])

  const { data: statuses } = useQuery({
    queryKey: ['custom-statuses', companyId],
    queryFn: () => customStatusesApi.list(companyId).then(r => r.data),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (dto: CreateLeadFormData) => leadsApi.create(companyId, dto),
    onSuccess: (res) => { reset(); toast.success('Lead created'); onSuccess?.(); onCreated?.(res.data) },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to create lead')
    },
  })

  const submit = (d: CreateLeadFormData) => mutation.mutate({
    ...d,
    email: d.email?.trim() || undefined,
    phone: d.phone?.trim() || undefined,
    source: d.source || undefined,
  })

  const statusOptions: DropdownOption[] = (statuses ?? [])
    .filter(s => s.applyTo === 'lead')
    .map(s => ({ value: s.label, label: s.label }))

  return (
    <Modal open={open} onClose={onClose} title="New Lead" size="md">
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">First Name *</label>
            <input {...register('firstName', { required: true })} className="input" />
          </div>
          <div>
            <label className="label">Last Name *</label>
            <input {...register('lastName', { required: true })} className="input" />
          </div>
        </div>
        <div>
          <label className="label">Email</label>
          <input
            {...register('email', { validate: () => hasContact || 'contact required' })}
            type="email"
            className={`input${showContactError ? ' border-red-400' : ''}`}
          />
        </div>
        <div>
          <label className="label">Phone</label>
          <input
            {...register('phone', { validate: () => hasContact || 'contact required' })}
            className={`input${showContactError ? ' border-red-400' : ''}`}
          />
        </div>
        {/* Neither field is required on its own — one of the two is. A single
            hint says that once, instead of an asterisk on both. */}
        <p className={`text-xs -mt-1.5 ${showContactError ? 'text-red-500' : 'text-gray-400'}`}>
          Fill in at least one of Email or Phone.
        </p>
        <div>
          <label className="label">Organization *</label>
          <Controller
            control={control}
            name="organizationId"
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownSelect
                value={field.value ?? ''}
                onChange={field.onChange}
                options={orgs.map(o => ({ value: o.id, label: o.name }))}
                placeholder="— Select organization —"
                searchable={orgs.length > 8}
                className={errors.organizationId ? 'border border-red-400 rounded-md' : ''}
              />
            )}
          />
          {errors.organizationId && <p className="text-xs text-red-500 mt-0.5">Required</p>}
        </div>
        <div>
          <label className="label">Status</label>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <DropdownSelect
                value={field.value ?? ''}
                onChange={field.onChange}
                options={statusOptions}
                placeholder="— Default —"
                onClear={() => field.onChange('')}
                clearLabel="Use default"
              />
            )}
          />
        </div>
        <div>
          <label className="label">Source</label>
          <Controller
            control={control}
            name="source"
            render={({ field }) => (
              <DropdownSelect
                value={field.value ?? ''}
                onChange={field.onChange}
                options={LEAD_SOURCE_OPTIONS}
                placeholder="— Select source —"
                onClear={() => field.onChange('')}
                clearLabel="No source"
              />
            )}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">
            Create Lead
          </button>
        </div>
      </form>
    </Modal>
  )
}
